import { useMutation, useQuery } from '@tanstack/react-query';
import { useApiConfig } from '@/app/ApiProvider';
import { apiFetch } from '@/app/api-fetch';
import type { components } from '@/api/types/api-gateway';

// SInviteAccept — invitation preview + accept hooks.
// GET /v1/invitations/{token} is unauthenticated (the token IS the
// auth). Same for POST /v1/invitations/{token}/accept. Using
// apiFetch directly here because the path-param substitution in
// openapi-fetch was inconsistent across the test env.
export type Invitation = components['schemas']['Invitation'];

export function useInvitationByToken(token: string) {
  const config = useApiConfig();
  return useQuery({
    queryKey: ['invitation', token],
    enabled: !!token,
    queryFn: async (): Promise<Invitation> => {
      const res = await apiFetch(
        config,
        `/v1/invitations/${encodeURIComponent(token)}`,
      );
      if (!res.ok) throw new Error('invitation not found');
      return (await res.json()) as Invitation;
    },
    retry: false,
    staleTime: 60_000,
  });
}

export function useAcceptInvitation(token: string) {
  const config = useApiConfig();
  return useMutation({
    mutationFn: async (vars: { displayName?: string; password: string }) => {
      const res = await apiFetch(
        config,
        `/v1/invitations/${encodeURIComponent(token)}/accept`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            display_name: vars.displayName,
            password: vars.password,
          }),
        },
      );
      if (!res.ok) {
        let msg = 'accept failed';
        try {
          const body = (await res.json()) as { error?: string };
          if (body?.error) msg = body.error;
        } catch {
          /* non-JSON body */
        }
        throw new Error(msg);
      }
      return res.json();
    },
  });
}
